import {DOCUMENT, inject, Injectable, Signal, signal} from '@angular/core';

// https://www.cookieyes.com/documentation/events-on-cookie-banner-interactions/
export type CookieYesCategory = 'necessary' | 'functional' | 'analytics' | 'performance' | 'advertisement';

interface CookieYesConsent {
  categories: Record<CookieYesCategory, boolean>;
}

interface CookieYesConsentUpdate {
  accepted: CookieYesCategory[];
  rejected: CookieYesCategory[];
}

declare global {
  interface Window {
    getCkyConsent?: () => CookieYesConsent;
  }
}

/**
 * Exposes the visitor's CookieYes consent as signals. Every category stays denied until
 * the banner script reports otherwise, so nothing is tracked before the visitor opts in.
 * Only meant to be used in the browser.
 */
@Injectable({
  providedIn: 'root'
})
export class ConsentService {

  private readonly document = inject(DOCUMENT);
  private readonly analyticsConsent = signal(false);
  private readonly advertisementConsent = signal(false);

  private readonly categoryMapping: Record<CookieYesCategory, Signal<boolean>> = {
    analytics: this.analyticsConsent.asReadonly(),
    advertisement: this.advertisementConsent.asReadonly(),
    //not implemented yet
    functional: signal(false),
    necessary: signal(false),
    performance: signal(false)
  };

  constructor() {
    const window = this.document.defaultView;
    if (!window) {
      return;
    }
    if (window.getCkyConsent) {
      this.apply(window.getCkyConsent().categories);
    }
    this.document.addEventListener('cookieyes_banner_load', event => {
      this.apply((event as CustomEvent<CookieYesConsent>).detail.categories);
    });
    this.document.addEventListener('cookieyes_consent_update', event => {
      const {accepted} = (event as CustomEvent<CookieYesConsentUpdate>).detail;
      this.analyticsConsent.set(accepted.includes('analytics'));
      this.advertisementConsent.set(accepted.includes('advertisement'));
    });
  }

  signalFor(category: CookieYesCategory) {
    return this.categoryMapping[category];
  }

  private apply(categories: Record<CookieYesCategory, boolean>) {
    this.analyticsConsent.set(categories.analytics);
    this.advertisementConsent.set(categories.advertisement);
  }

}
